<template>
  <div class="users-panel">
    <div class="panel-header">
      <span>{{ users.length }} usuario(s)</span>
      <Button label="Agregar usuario" icon="pi pi-plus" size="small" @click="showForm = true" />
    </div>

    <DataTable :value="users" :loading="loading" size="small" responsiveLayout="scroll">
      <Column field="full_name" header="Nombre" />
      <Column field="email" header="Email" />
      <Column field="role" header="Rol">
        <template #body="{ data }">
          <Tag :value="data.role" severity="info" size="small" />
        </template>
      </Column>
      <Column header="">
        <template #body="{ data }">
          <div class="row-actions">
            <Button icon="pi pi-key" text rounded size="small" v-tooltip="'Permisos por agente'" @click="openPermissions(data)" />
            <Button icon="pi pi-user-minus" text rounded size="small" severity="warning" v-tooltip="'Inactivar usuario'" @click="deactivateUser(data)" />
            <Button icon="pi pi-trash" text rounded size="small" severity="danger" v-tooltip="'Remover del tenant'" @click="removeUser(data)" />
          </div>
        </template>
      </Column>
      <template #empty><p class="empty-msg">No hay usuarios en este tenant.</p></template>
    </DataTable>

    <Dialog v-model:visible="showForm" header="Agregar usuario" modal :style="{ width: '440px' }">
      <div class="add-form">
        <div class="field">
          <label>Email <span class="req">*</span></label>
          <InputText v-model="form.email" type="email" class="w-full" placeholder="usuario@empresa.com" />
        </div>
        <div class="field">
          <label>Nombre completo <span class="req">*</span></label>
          <InputText v-model="form.full_name" class="w-full" />
        </div>
        <div class="field">
          <label>Contraseña</label>
          <PasswordField v-model="form.password" placeholder="Dejar vacío para no cambiar" :auto-generate="true" />
        </div>
        <div class="field">
          <label>Rol</label>
          <Dropdown v-model="form.role" :options="roles" optionLabel="label" optionValue="value" class="w-full" />
        </div>
        <Message v-if="formError" severity="error" :closable="false" size="small">{{ formError }}</Message>
      </div>
      <template #footer>
        <Button label="Cancelar" severity="secondary" @click="showForm = false" />
        <Button label="Agregar" icon="pi pi-check" :loading="saving" @click="addUser" />
      </template>
    </Dialog>

    <Dialog
      v-model:visible="showPermissionsDialog"
      :header="`Permisos por agente — ${selectedUser?.full_name || ''}`"
      modal
      :style="{ width: '760px' }"
    >
      <UserAgentPermissionsPanel
        v-if="selectedUser"
        :user-id="selectedUser.user_id"
        @updated="load"
      />
    </Dialog>

    <Toast />
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { useToast } from 'primevue/usetoast';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import Button from 'primevue/button';
import Tag from 'primevue/tag';
import Dialog from 'primevue/dialog';
import InputText from 'primevue/inputtext';
import Dropdown from 'primevue/dropdown';
import Message from 'primevue/message';
import Toast from 'primevue/toast';
import PasswordField from '../common/PasswordField.vue';
import UserAgentPermissionsPanel from './UserAgentPermissionsPanel.vue';
import api from '../../services/api';

const props = defineProps({ tenantId: { type: String, required: true } });
const toast = useToast();
const users = ref([]);
const loading = ref(false);
const showForm = ref(false);
const showPermissionsDialog = ref(false);
const saving = ref(false);
const formError = ref('');
const selectedUser = ref(null);
const form = ref({ email: '', full_name: '', password: '', role: 'tenant_admin' });
const roles = [
  { label: 'Administrador del tenant', value: 'tenant_admin' },
  { label: 'Usuario', value: 'tenant_user' },
  { label: 'Solo lectura', value: 'viewer' },
  { label: 'Auditor', value: 'auditor' },
];

const load = async () => {
  loading.value = true;
  try { const res = await api.get(`/tenants/${props.tenantId}/users`); users.value = res.data; }
  catch (e) { console.error(e); }
  finally { loading.value = false; }
};

const addUser = async () => {
  if (!form.value.email || !form.value.full_name) { formError.value = 'Email y nombre son obligatorios'; return; }
  saving.value = true; formError.value = '';
  try {
    await api.post(`/tenants/${props.tenantId}/users`, form.value);
    showForm.value = false;
    form.value = { email: '', full_name: '', password: '', role: 'tenant_admin' };
    await load();
    toast.add({ severity: 'success', summary: 'Usuario agregado', life: 2500 });
  } catch (e) {
    formError.value = e.response?.data?.detail || e.message;
  } finally { saving.value = false; }
};

const removeUser = async (user) => {
  try {
    await api.delete(`/tenants/${props.tenantId}/users/${user.user_id}`);
    await load();
    toast.add({ severity: 'success', summary: 'Usuario removido', life: 2500 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail, life: 3000 });
  }
};

const openPermissions = (user) => {
  selectedUser.value = user;
  showPermissionsDialog.value = true;
};

const deactivateUser = async (user) => {
  try {
    await api.post(`/users/${user.user_id}/deactivate`);
    await load();
    toast.add({ severity: 'success', summary: 'Usuario inactivado', life: 2500 });
  } catch (e) {
    toast.add({ severity: 'error', summary: 'Error', detail: e.response?.data?.detail || e.message, life: 3000 });
  }
};

onMounted(load);
</script>

<style scoped>
.users-panel { display: flex; flex-direction: column; gap: 0.75rem; }
.panel-header { display: flex; align-items: center; justify-content: space-between; }
.empty-msg { text-align: center; color: #9ca3af; padding: 1rem; }
.add-form { display: flex; flex-direction: column; gap: 0.75rem; }
.field { display: flex; flex-direction: column; gap: 0.3rem; }
.field label { font-weight: 600; font-size: 0.82rem; color: #374151; }
.req { color: #ef4444; }
.w-full { width: 100%; }
.row-actions { display: inline-flex; gap: 0.2rem; }
</style>
